import React, { useState, useEffect } from 'react';
import './SignUpPage.css';
import Logo from './Logo';
import EyeIcon from './EyeIcon';

const SignUpPage = ({ onBack, onNavigateToLogin, onNavigateToAbout, onNavigateToTerms, onNavigateToPrivacy }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTimeout, setPasswordTimeout] = useState(null);
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const [showMobileProfileMenu, setShowMobileProfileMenu] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-hide password after 3 seconds of typing
    if (name === 'password' && value.length > 0) {
      if (passwordTimeout) {
        clearTimeout(passwordTimeout);
      }
      const timeout = setTimeout(() => {
        setShowPassword(false);
      }, 3000);
      setPasswordTimeout(timeout);
    }
  };

  useEffect(() => {
    return () => {
      if (passwordTimeout) {
        clearTimeout(passwordTimeout);
      }
    };
  }, [passwordTimeout]);

  // Close mobile menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      try {
        if (!e || !e.target) return;
        
        if (showMobileProfileMenu && !e.target.closest('.mobile-profile-icon') && !e.target.closest('.mobile-profile-menu')) {
          setShowMobileProfileMenu(false);
        }
        if (showMobileNavMenu && !e.target.closest('.mobile-hamburger') && !e.target.closest('.mobile-nav-dropdown')) {
          setShowMobileNavMenu(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in click outside handler:', error);
        }
      }
    };

    try {
      document.addEventListener('click', handleClickOutside);
      return () => {
        try {
          document.removeEventListener('click', handleClickOutside);
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up click outside listener:', error);
      }
      return () => {};
    }
  }, [showMobileProfileMenu, showMobileNavMenu]);

  const showNotification = (message) => {
    try {
      if (typeof document === 'undefined' || !document.body) return;
      if (!message) return;

      const notification = document.createElement('div');
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 100px;
        left: 50%;
        transform: translateX(-50%) translateY(-20px);
        background-color: #2d3649;
        color: #ffffff;
        padding: 16px 32px;
        border-radius: 8px;
        font-size: 1rem;
        font-family: 'DM Sans', sans-serif;
        font-weight: 600;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        z-index: 9999999;
        pointer-events: none;
        white-space: nowrap;
        opacity: 0;
        transition: all 0.3s ease;
      `;

      if (document.body) {
        document.body.appendChild(notification);

        requestAnimationFrame(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '1';
              notification.style.transform = 'translateX(-50%) translateY(0)';
            }
          } catch (error) {
            // Silently handle animation errors
          }
        });

        setTimeout(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '0';
              notification.style.transform = 'translateX(-50%) translateY(-20px)';
              setTimeout(() => {
                try {
                  if (notification && notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                  }
                } catch (error) {
                  // Silently handle cleanup errors
                }
              }, 300);
            }
          } catch (error) {
            // Silently handle errors
          }
        }, 3000);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in showNotification:', error);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    showNotification('Accounts launching soon with the app!');
  };

  const handleOAuthClick = (e) => {
    e.preventDefault();
    showNotification('Accounts launching soon with the app!');
  };

  return (
    <div className="signup-page">
      {/* Navigation Bar - sticky as per JSON nav_bar.sticky: true */}
      <nav className="navigation">
        <div className="container">
          <div className="nav-content">
            {/* Mobile Hamburger Menu */}
            <div className="mobile-nav-controls">
              <button
                className={`mobile-hamburger ${showMobileNavMenu ? 'active' : ''}`}
                onClick={() => setShowMobileNavMenu(!showMobileNavMenu)}
                aria-label="Navigation menu"
              >
                {/* Hamburger lines */}
                <span></span>
                <span></span>
                <span></span>
                {/* Logo icon - appears when active (mobile only) */}
                <img 
                  className="hamburger-logo-icon"
                  src="/Images/holologo_mobile.svg"
                  alt="DermaMatch Logo"
                />
              </button>
            </div>

            {/* Logo placement: top left as per JSON */}
            <div className="nav-logo">
              <button 
                className="logo-button" 
                onClick={onBack}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Logo size="medium" />
              </button>
            </div>
            
            {/* Navigation items - centered as per JSON nav_items */}
            <div className="nav-items">
              <button onClick={onBack} className="nav-link">
                DISCOVER
              </button>
              <button onClick={onNavigateToAbout} className="nav-link">
                ABOUT
              </button>
            </div>
            
            {/* User actions - right aligned */}
            <div className="nav-actions">
              <button className="btn btn-secondary nav-btn" onClick={onNavigateToLogin}>LOG IN</button>
              <button className="btn btn-primary nav-btn">SIGN UP</button>
            </div>

            {/* Mobile Profile Icon */}
            <button
              className="mobile-profile-icon"
              onClick={() => setShowMobileProfileMenu(!showMobileProfileMenu)}
              aria-label="Profile menu"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNavMenu && (
            <div className="mobile-nav-dropdown">
              <button
                onClick={() => { onBack(); setShowMobileNavMenu(false); }}
              >
                DISCOVER
              </button>
              <button
                onClick={() => { onNavigateToAbout(); setShowMobileNavMenu(false); }}
              >
                ABOUT
              </button>
            </div>
          )}

          {/* Mobile Profile Menu */}
          {showMobileProfileMenu && (
            <div className="mobile-profile-menu">
              <button onClick={() => { setShowMobileProfileMenu(false); }}>
                SIGN UP
              </button>
              <button onClick={() => { onNavigateToLogin(); setShowMobileProfileMenu(false); }}>
                LOG IN
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content with Background Gradient */}
      <div className="signup-main-content">
        <div className="background-gradient"></div>
        <div className="signup-container">
          <div className="signup-card">
            {/* Sign Up Title */}
            <div className="signup-title">
              <img src="/Images/Sign Up.svg" alt="Sign Up" />
            </div>
            
            {/* OAuth Icons */}
            <div className="oauth-section">
              <div className="oauth-icons">
                <button className="oauth-btn google-btn" onClick={handleOAuthClick}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </button>
                <button className="oauth-btn apple-btn" onClick={handleOAuthClick}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="#000000"/>
                  </svg>
                </button>
                <button className="oauth-btn microsoft-btn" onClick={handleOAuthClick}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="10.5" height="10.5" fill="#F25022"/>
                    <rect x="12.5" y="1" width="10.5" height="10.5" fill="#7FBA00"/>
                    <rect x="1" y="12.5" width="10.5" height="10.5" fill="#00A4EF"/>
                    <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="separator">
              <span className="separator-text">OR</span>
            </div>

            {/* Sign Up Form */}
            <form onSubmit={handleSubmit} className="signup-form">
              <div className="form-group">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                  className="form-field"
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="form-field"
                />
              </div>
              <div className="form-group">
                <div className="password-field-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                    className="form-field"
                  />
                  <EyeIcon 
                    isVisible={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary signup-btn">
                SIGN UP
              </button>
            </form>

            {/* Terms and Conditions */}
            <p className="terms-text">
              By signing up, you agree to our{' '}
              <a 
                href="#" 
                className="terms-link" 
                onClick={(e) => { e.preventDefault(); if(onNavigateToTerms) onNavigateToTerms(); }}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a 
                href="#" 
                className="terms-link" 
                onClick={(e) => { e.preventDefault(); if(onNavigateToPrivacy) onNavigateToPrivacy(); }}
              >
                Privacy Policy
              </a>.
            </p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
